import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Typography,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { LoanDetail, EmiPaymentHistory } from "../types/loan";
import { useState, useEffect } from "react";
import { useFirebaseAuth } from "../hooks/useFirebaseAuth";
import axios from "axios";
import { useToast } from "./Toast";
import { formatCurrency, formatDate } from "../utils/utils";
import { PlanStatus } from "../utils/alertUtils";
import { getValidToken } from "../utils/authUtils";

interface LoanPaymentsProps {
  data: LoanDetail;
  planStatus: PlanStatus;
}

const LoanPayments = ({ data, planStatus }: LoanPaymentsProps) => {
  const localUser = sessionStorage.getItem("user");
  const companyId = localUser ? JSON.parse(localUser).companyId : null;
  const [emiPaymentHistory, setEmiPaymentHistory] = useState<
    EmiPaymentHistory[]
  >([]);
  const [selectedPayments, setSelectedPayments] = useState<EmiPaymentHistory[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedEmi, setSelectedEmi] = useState<EmiPaymentHistory | null>(
    null
  );
  const [customPaymentDate, setCustomPaymentDate] = useState<string>("");
  const { showToast } = useToast();

  useEffect(() => {
    if (data.id) {
      fetchLoanPayments();
    }
  }, [data.id]);

  const fetchLoanPayments = async () => {
    setIsLoading(true);
    try {
      const token = await getValidToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST}vehicle-finance/loans/${data.id}/emi-payment-history`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.isSuccess) {
        setEmiPaymentHistory(response.data.data?.emiPaymentHistory || []);
      } else {
        showToast("Failed to fetch loan payments", "error");
      }
    } catch (error) {
      console.error("Error fetching loan payments:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentDateConfirm = (onTime: boolean) => {
    if (!selectedEmi) return;

    if (onTime) {
      // If payment was on time, use the EMI due date
      setSelectedPayments((prev) => [...prev, selectedEmi]);
      setOpenDialog(false);
      setSelectedEmi(null);
    } else {
      // If payment was not on time, keep dialog open for custom date
      setCustomPaymentDate(new Date().toISOString().split("T")[0]);
    }
  };

  const handleCustomDateConfirm = () => {
    if (!selectedEmi || !customPaymentDate) return;

    setSelectedPayments((prev) => [
      ...prev,
      { ...selectedEmi, emiDate: customPaymentDate },
    ]);
    setOpenDialog(false);
    setSelectedEmi(null);
    setCustomPaymentDate("");
  };

  const getEmiStatus = (emiPayment: EmiPaymentHistory) => {
    const today = new Date();
    const dueDate = new Date(emiPayment.emiDate);

    if (emiPayment.isPaid) {
      return { label: "Paid", color: "success" as const };
    }

    if (dueDate < today && !emiPayment.isPaid) {
      return { label: "Late", color: "error" as const };
    }

    return { label: "Upcoming", color: "info" as const };
  };

  const handleMarkPaid = async () => {
    setIsMarkingPaid(true);
    try {
      const selectedPaymentIds = new Set(selectedPayments.map((p) => p.id));

      // Create a map of selected payment dates
      const selectedPaymentDates = new Map(
        selectedPayments.map((p) => [p.id, p.emiDate])
      );

      const emiPayments = emiPaymentHistory?.map((payment) => ({
        ...payment,
        id: payment.id,
        isPaid: payment.isPaid || selectedPaymentIds.has(payment.id),
        paymentDate: selectedPaymentIds.has(payment.id)
          ? selectedPaymentDates.get(payment.id) // Use the stored custom payment date
          : payment.paymentDate, // Keep existing payment date for others
      }));

      const token = await getValidToken();
      const response = await axios.put(
        `${process.env.REACT_APP_API_HOST}vehicle-finance/loans/${data.id}/emi-payment-history`,
        emiPayments,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.isSuccess) {
        showToast("EMIs marked as paid successfully", "success");
        fetchLoanPayments();
        setSelectedPayments([]);
      } else {
        showToast("Failed to update EMI payments", "error");
      }
    } catch (error) {
      console.error("Error updating EMI payments:", error);
      showToast("Failed to update EMI payments", "error");
    } finally {
      setIsMarkingPaid(false);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  if (isMarkingPaid) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container spacing={0.5} mt={0.5}>
      <Grid item xs={12}>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography variant="subtitle1" fontSize="0.75rem">
            Total EMIs: {emiPaymentHistory?.length}
          </Typography>
          <Box>
            <Chip
              label={`Paid: ${
                emiPaymentHistory?.filter((p) => p.isPaid)?.length || 0
              }`}
              color="success"
              size="small"
              sx={{ mr: 1, fontSize: "0.7rem" }}
              variant="outlined"
            />
            <Chip
              label={`Late: ${
                emiPaymentHistory?.filter(
                  (p) => !p.isPaid && new Date(p.emiDate) < new Date()
                )?.length || 0
              }`}
              color="error"
              size="small"
              sx={{ mr: 1, fontSize: "0.7rem" }}
              variant="outlined"
            />
            {planStatus?.permissions?.canEditVehicleFinance &&
              planStatus?.permissions?.canCreateVehicleFinance && (
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ ml: 1, py: 0.5, fontSize: "0.7rem" }}
                  disabled={selectedPayments.length === 0}
                  onClick={handleMarkPaid}
                >
                  Mark as Paid
                </Button>
              )}
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ maxHeight: 300, overflow: "auto" }}>
          <List dense disablePadding sx={{ py: 0 }}>
            {emiPaymentHistory?.map((emiPayment, index) => {
              const status = getEmiStatus(emiPayment);
              const selectedPayment = selectedPayments.find(
                (p) => p.id === emiPayment.id
              );
              return (
                <ListItem
                  key={emiPayment.id || index}
                  divider
                  secondaryAction={
                    <Box display="flex" alignItems="center" gap={2}>
                      {status.label !== "Upcoming" && (
                        <Chip
                          label={status.label}
                          color={status.color}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: "0.65rem" }}
                        />
                      )}
                      <Typography fontSize="0.75rem">
                        {formatCurrency(emiPayment.amount)}
                      </Typography>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={
                      <>
                        <Checkbox
                          checked={
                            emiPayment.isPaid ||
                            selectedPayments.some((p) => p.id === emiPayment.id)
                          }
                          disabled={emiPayment.isPaid}
                          onChange={() => {
                            if (
                              !planStatus?.permissions?.canEditVehicleFinance ||
                              !planStatus?.permissions?.canCreateVehicleFinance
                            ) {
                              showToast(
                                "You don't have permission to edit EMI payments",
                                "warning"
                              );
                              return;
                            }
                            if (emiPayment.isPaid) return;
                            if (
                              selectedPayments.some(
                                (p) => p.id === emiPayment.id
                              )
                            ) {
                              setSelectedPayments(
                                selectedPayments.filter(
                                  (p) => p.id !== emiPayment.id
                                )
                              );
                            } else {
                              setSelectedEmi(emiPayment);
                              setOpenDialog(true);
                            }
                          }}
                          sx={{
                            "&.Mui-disabled": {
                              color: "success.main",
                            },
                          }}
                          size="small"
                        />
                        {emiPayment.emiNumber && (
                          <Typography
                            fontSize="0.75rem"
                            component="span"
                            mr={1}
                          >
                            EMI {emiPayment.emiNumber}
                          </Typography>
                        )}
                        <Typography fontSize="0.7rem" component="span">
                          Due date: {formatDate(emiPayment.emiDate)}
                        </Typography>
                        {selectedPayment?.emiDate && (
                          <Typography
                            component="span"
                            sx={{ ml: 2 }}
                            variant="caption"
                            fontSize="0.65rem"
                          >
                            Selected Payment Date:{" "}
                            {formatDate(selectedPayment?.emiDate || "")}
                          </Typography>
                        )}
                        {emiPayment.paymentDate && (
                          <Typography
                            component="span"
                            sx={{ ml: 2 }}
                            variant="caption"
                            fontSize="0.65rem"
                          >
                            Paid on: {formatDate(emiPayment.paymentDate)}
                          </Typography>
                        )}
                      </>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        </Paper>
      </Grid>

      <Dialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setSelectedEmi(null);
          setCustomPaymentDate("");
        }}
      >
        <DialogTitle sx={{ fontSize: "0.8rem" }}>
          Payment Confirmation
        </DialogTitle>
        <DialogContent>
          {!customPaymentDate ? (
            <Typography fontSize="0.75rem">
              Was this EMI paid on time (
              {formatDate(selectedEmi?.emiDate || "")}
              )?
            </Typography>
          ) : (
            <Box sx={{ mt: 2 }}>
              <Typography gutterBottom fontSize="0.75rem">
                Please select the actual payment date:
              </Typography>
              <TextField
                type="date"
                value={customPaymentDate}
                onChange={(e) => setCustomPaymentDate(e.target.value)}
                fullWidth
                size="small"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!customPaymentDate ? (
            <>
              <Button
                onClick={() => handlePaymentDateConfirm(true)}
                color="primary"
                size="small"
                sx={{ fontSize: "0.7rem" }}
              >
                Yes, On Time
              </Button>
              <Button
                onClick={() => handlePaymentDateConfirm(false)}
                color="primary"
                size="small"
                sx={{ fontSize: "0.7rem" }}
              >
                No, Different Date
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={handleCustomDateConfirm}
                color="primary"
                disabled={!customPaymentDate}
                size="small"
                sx={{ fontSize: "0.7rem" }}
              >
                Confirm Date
              </Button>
              <Button
                onClick={() => {
                  setCustomPaymentDate("");
                  setOpenDialog(false);
                  setSelectedEmi(null);
                }}
                color="primary"
                size="small"
                sx={{ fontSize: "0.7rem" }}
              >
                Cancel
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default LoanPayments;
